import React, { useEffect, useState } from 'react';
import { MapPin, MapPinOff } from 'lucide-react';
import { isIOS, isAndroid, isStandalone } from '../lib/push';

// Reads the browser's geolocation permission state ('granted' | 'prompt' | 'denied' | 'unknown')
export function useLocationPermission() {
  const [state, setState] = useState('unknown');
  useEffect(() => {
    if (!navigator.permissions?.query) return undefined;
    let status;
    navigator.permissions.query({ name: 'geolocation' })
      .then((s) => { status = s; setState(s.state); s.onchange = () => setState(s.state); })
      .catch(() => setState('unknown'));
    return () => { if (status) status.onchange = null; };
  }, []);
  return state;
}

const STEPS = () => {
  if (isIOS()) {
    return isStandalone()
      ? ['When your phone asks, tap Allow — the Home-Screen app remembers it.',
         'Still asking? iPhone Settings → Privacy & Security → Location Services → Safari Websites → While Using.']
      : ['When Safari asks, tap Allow.',
         'To stop it asking every time: tap aA in the address bar → Website Settings → Location → Allow.',
         'Or iPhone Settings → Safari → Location → Allow.'];
  }
  if (isAndroid()) {
    return ['When Chrome asks, choose Allow (NOT "Only this time") — it will never ask again for 365Ops.',
            'To fix it later: Chrome ⋮ → Settings → Site settings → Location → 365Ops → Allow.'];
  }
  return ['Click Allow on the browser prompt; it is remembered for this site.',
          'Blocked? Click the lock icon in the address bar → Site settings → Location → Allow.'];
};

// Small status line + per-phone instructions so crews can stop the location prompt showing every time.
export default function LocationPermissionHint({ className = '', alwaysShowSteps = false }) {
  const state = useLocationPermission();
  const [open, setOpen] = useState(alwaysShowSteps);
  const label = state === 'granted' ? 'Location: allowed — no more prompts'
    : state === 'denied' ? 'Location: blocked on this phone'
    : state === 'prompt' ? 'Location: asks every time'
    : 'Location: tap Acknowledge to allow';
  const cls = state === 'granted' ? 'text-emerald-700' : state === 'denied' ? 'text-red-600' : 'text-amber-700';
  const Icon = state === 'denied' ? MapPinOff : MapPin;
  const showSteps = open || state === 'denied';
  return (
    <div className={`text-xs ${className}`} data-testid="location-permission-hint">
      <button type="button" onClick={() => setOpen(o => !o)} className={`inline-flex items-center gap-1 ${cls} hover:underline`} data-testid="location-permission-state">
        <Icon className="w-3.5 h-3.5" /> {label}{state !== 'granted' && <span className="text-slate-500"> · how to stop it asking</span>}
      </button>
      {showSteps && state !== 'granted' && (
        <ol className="mt-1.5 ml-4 list-decimal space-y-0.5 text-slate-700" data-testid="location-permission-steps">
          {STEPS().map((s, i) => <li key={i}>{s}</li>)}
        </ol>
      )}
    </div>
  );
}
